// utils/coursePolling.js
import { UNIVERSITY_API_URL } from '../config';

// Mirrors CourseContentProcessingStatus in university-service.
const STATUS = {
    PENDING: 'pending',
    READY: 'ready',
    FAILED: 'failed',
};

const GENERIC_FAILURE =
    'This course content could not be processed. Please check the file and try uploading it again.';

const fetchCourse = async (courseId) => {
    const token = JSON.parse(localStorage.getItem('user'))?.token;

    const response = await fetch(
        `${UNIVERSITY_API_URL}/universitycourse/${courseId}/?includes=`,
        {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }
    );

    if (!response.ok) {
        throw new Error(`Failed to fetch course: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
};

/**
 * Polls a course until its content has been processed by the S3 Lambda.
 *
 * The upload is fire-and-forget - the browser PUTs the zip to S3 and a Lambda
 * picks it up from an S3 event - so the course record is the only channel the
 * importer has back to the user. It writes contentProcessingStatus, and on
 * failure a message explaining why, which is thrown here so the caller can show
 * the real reason instead of a timeout.
 *
 * @param {string} courseId - The internal course ID
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @param {number} intervalMs - Milliseconds between polls
 * @returns {Promise<Object>} The processed course data
 */
export const pollForCourseReady = async (
    courseId,
    maxAttempts = 90,
    intervalMs = 2000
) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const course = await fetchCourse(courseId);

            if (course.contentProcessingStatus === STATUS.FAILED) {
                const failure = new Error(course.contentProcessingError || GENERIC_FAILURE);
                // Flagged so the retry handler below can tell a verdict from a
                // failed attempt and stop polling immediately.
                failure.isCourseProcessingFailure = true;
                failure.errorCode = course.contentProcessingErrorCode || '';
                throw failure;
            }

            if (course.contentProcessingStatus === STATUS.READY) {
                console.log(`Course ${courseId} is ready after ${attempt + 1} attempts`);
                return course;
            }

            // Courses uploaded before contentProcessingStatus existed never get a
            // status written, so a populated path still counts as done.
            if (
                course.contentProcessingStatus !== STATUS.PENDING &&
                course.courseContentPath &&
                course.courseContentPath.length > 0
            ) {
                console.log(`Course ${courseId} is ready after ${attempt + 1} attempts`);
                return course;
            }

            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, intervalMs));

        } catch (error) {
            // A recorded processing failure is the answer, not a failed attempt -
            // retrying it would just burn the remaining attempts.
            if (error.isCourseProcessingFailure) {
                throw error;
            }

            console.error(`Polling attempt ${attempt + 1} failed:`, error);

            // If it's the last attempt, throw the error
            if (attempt === maxAttempts - 1) {
                throw error;
            }

            // Otherwise, wait and try again
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
    }

    throw new Error(
        'This course is taking longer than expected to process. It may still finish - ' +
        'refresh the course list shortly to check.'
    );
};

/**
 * Checks if a course has been fully processed
 * @param {string} courseId - The internal course ID
 * @returns {Promise<boolean>} True if course is ready
 */
export const isCourseReady = async (courseId) => {
    try {
        const course = await fetchCourse(courseId);

        if (course?.contentProcessingStatus === STATUS.FAILED) {
            return false;
        }

        return !!(course?.courseContentPath);

    } catch (error) {
        console.error('Error checking course status:', error);
        return false;
    }
};
